import React, { useEffect, useRef, useState } from "react";
import { useModalBackdropClose } from "../hooks/useModalBackdropClose";

export interface SMRTTasksRejectModalProps {
  isOpen: boolean;
  taskTitle?: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void> | void;
}

export default function SMRTTasksRejectModal({
  isOpen,
  taskTitle,
  onClose,
  onSubmit,
}: SMRTTasksRejectModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReason("");
    setError(null);
    setSaving(false);
    setTimeout(() => ref.current?.focus(), 0);
  }, [isOpen]);

  // Hook must come before the early return so the order stays stable.
  const backdrop = useModalBackdropClose(() => {
    if (saving) return;
    onClose();
  });

  if (!isOpen) return null;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      setError("Please provide a reason");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSubmit(reason.trim());
    } catch (err: unknown) {
      setError((err as Error)?.message || "Failed to reject");
      setSaving(false);
    }
  };

  return (
    <div className="smrttasks-modal-overlay" {...backdrop}>
      <div className="smrttasks-modal" onClick={(e) => e.stopPropagation()}>
        <div className="smrttasks-modal-header">
          <h2>Reject Task</h2>
        </div>
        <div className="smrttasks-modal-body">
          {error && <p className="smrttasks-modal-error">{error}</p>}
          {taskTitle && (
            <p
              style={{
                color: "#9aa0a6",
                fontSize: "0.85rem",
                margin: "0 0 12px",
              }}
            >
              {taskTitle}
            </p>
          )}

          <div className="smrttasks-form-row">
            <label htmlFor="smrttasks-reject-reason">Reason for rejection</label>
            <textarea
              id="smrttasks-reject-reason"
              ref={ref}
              className="smrttasks-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={saving}
              placeholder="Why is this task being rejected?"
            />
          </div>

          <div className="smrttasks-modal-actions">
            <button
              type="button"
              className="smrttasks-btn-cancel"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="smrttasks-btn-save smrttasks-btn-warning-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Rejecting..." : "Reject Task"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
